/**
 * ModelTierPicker — AC-AGS-M4-7
 *
 * A popover picker that opens when the user clicks the model badge.
 * Allows switching between Standard, Complex, or "Use actor default".
 * Calls PATCH /api/v1/agent-sessions/:id/model-tier on selection.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Cpu, ChevronDown, Check } from 'lucide-react';
import { formatNumber } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';
import { useNotificationStore } from '@/stores/notificationStore';
import { Button } from '@/components/ui/Button';

export interface ModelTierPickerProps {
  sessionId: string;
  provider: string;
  model: string;
  modelTierOverride: 'standard' | 'complex' | null | undefined;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  onTierChanged?: (tier: 'standard' | 'complex' | null, resolvedModel: string | null) => void;
}

const TIER_OPTIONS: Array<{ tier: 'standard' | 'complex' | null; label: string; description: string }> = [
  { tier: null, label: 'Actor Default', description: 'Use the actor definition\'s model tier' },
  { tier: 'standard', label: 'Standard', description: 'Faster, lower cost' },
  { tier: 'complex', label: 'Complex', description: 'More capable, higher cost' },
];

export function ModelTierPicker({
  sessionId,
  provider,
  model,
  modelTierOverride,
  inputTokens,
  outputTokens,
  costUsd,
  onTierChanged,
}: ModelTierPickerProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pushNotification = useNotificationStore((s) => s.push);

  const totalTokens = inputTokens + outputTokens;
  const costStr = costUsd > 0 ? `$${costUsd.toFixed(4)}` : '';

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleSelect = async (tier: 'standard' | 'complex' | null) => {
    if (tier === modelTierOverride) {
      setOpen(false);
      return;
    }
    // Save previous value so we can revert on error
    const prevTier = modelTierOverride ?? null;
    setLoading(true);
    try {
      const result = await agentSessionsService.patchModelTier(sessionId, tier);
      onTierChanged?.(tier, result.resolvedModel ?? null);
      setOpen(false);

      const tierLabel = tier === null ? t('actor default') : tier === 'complex' ? t('Complex') : t('Standard');
      pushNotification({ type: 'success', message: `${t('Model switched to')} ${tierLabel}. ${t('Takes effect on next turn.')}` });
    } catch (err: any) {
      // Revert optimistic state and show error feedback
      onTierChanged?.(prevTier, null);
      const message = err?.message ?? t('Failed to switch model tier. Please try again.');
      pushNotification({ type: 'error', message });
    } finally {
      setLoading(false);
    }
  };

  const currentTierLabel = modelTierOverride
    ? modelTierOverride === 'complex' ? t('Complex') : t('Standard')
    : t('Default');

  return (
    <div className="relative" ref={ref}>
      {/* Badge button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-500 hover:bg-slate-100 hover:border-slate-300 h-auto"
        data-testid="model-badge"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('Model tier picker')}
      >
        <Cpu className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
        <span className="font-medium text-slate-700" data-testid="model-badge-provider">{provider}</span>
        <span className="text-slate-400">·</span>
        <span data-testid="model-badge-model">{model}</span>
        {modelTierOverride && (
          <>
            <span className="text-slate-300">|</span>
            <span className="text-purple-600 font-medium" data-testid="model-badge-tier">{currentTierLabel}</span>
          </>
        )}
        {totalTokens > 0 && (
          <>
            <span className="text-slate-300">|</span>
            <span data-testid="model-badge-tokens">{formatNumber(totalTokens)} {t('tokens')}</span>
            {costStr && (
              <>
                <span className="text-slate-300">·</span>
                <span data-testid="model-badge-cost">{costStr}</span>
              </>
            )}
          </>
        )}
        <ChevronDown className="h-3 w-3 text-slate-400" />
      </Button>

      {/* Dropdown */}
      {open && (
        <div
          className="absolute bottom-full mb-1 left-0 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden"
          role="listbox"
          aria-label={t('Select model tier')}
          data-testid="model-tier-picker"
        >
          <div className="px-3 py-2 border-b border-slate-100 text-xs text-slate-500 font-medium">
            {t('Model Tier')}
          </div>
          {TIER_OPTIONS.map(({ tier, label, description }) => {
            const isSelected = tier === (modelTierOverride ?? null);
            return (
              <Button
                key={String(tier)}
                variant="ghost"
                size="sm"
                role="option"
                aria-selected={isSelected}
                disabled={loading}
                onClick={() => handleSelect(tier)}
                className={`w-full justify-start text-left px-3 py-2 h-auto flex items-start gap-2 rounded-none hover:bg-slate-50 ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                data-testid={`model-tier-option-${tier ?? 'default'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{t(label)}</div>
                  <div className="text-xs text-slate-500">{t(description)}</div>
                </div>
                {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
